import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import {
  Box,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { useTranslation } from "react-i18next";
import {
  getPnlData,
  getPnlFilterOptions,
} from "../../viewServices/calls/apiCalls";
import { toast } from "react-toastify";
import AuthContext from "../../../../connections/auth/AuthProvider";
import PropTypes from "prop-types";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import FinancialImpactPnLDisplay from "./FinancialImpactPnLDisplay";
import { FilterSystem } from "../../sharedComponents/FilterSystem/index.js";

const financialImpactTabStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  containerBox: {
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    height: "100%",
    overflow: "hidden",
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: "1",
    maxWidth: "100%",
    overflow: "visible",
  },
  addScenarioBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    flex: "0 0 auto",
  },
  filtersBox: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1.5),
    flex: "1",
    maxWidth: "900px",
    overflow: "hidden",
  },
  divider: {
    width: "1px",
    backgroundColor: grayColor[4],
    alignSelf: "stretch",
    margin: `${theme.spacing(1)}px 0`,
  },
  filterFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 130,
    maxWidth: 150,
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 200,
    maxWidth: 250,
    margin: "7px 0 7px 0 !important",
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: clientColor + "!important",
      },
    },
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  clearFiltersButton: {
    marginLeft: theme.spacing(1),
    minHeight: "40px",
    minWidth: "40px",
    borderColor: grayColor[4],
    color: grayColor[1],
    "&:hover": {
      backgroundColor: grayColor[11],
      color: grayColor[1],
    },
  },
});

FinancialImpactTab.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isLoading: PropTypes.bool,
};

FinancialImpactTab.displayName = "FinancialImpactTab";

export default function FinancialImpactTab({
  scenarios,
  folders = [],
  isLoading = false,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  const useStyles = makeStyles((theme) =>
    financialImpactTabStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t, i18n } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // State management - no persistence, clears on page refresh
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const [pnlData, setPnlData] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errors, setErrors] = useState({});
  const [drillDownsOpen, setDrillDownsOpen] = useState({});

  // Filter state management - STANDARD 5 dimensions only
  const [selectedFilters, setSelectedFilters] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Filter options state - STANDARD 5 dimensions only
  const [filterOptions, setFilterOptions] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Special parameter - separate from FilterSystem
  const [selectedPnlFlag, setSelectedPnlFlag] = useState("");
  const [pnlFlagOptions, setPnlFlagOptions] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  // Filter dimensions configuration - STANDARD 5 only
  const filterDimensions = [
    { key: "products", label: t("overview.filter_dimensions.products") },
    { key: "brands", label: t("overview.filter_dimensions.brands") },
    { key: "channels", label: t("overview.filter_dimensions.channels") },
    { key: "regions", label: t("overview.filter_dimensions.regions") },
    { key: "clients", label: t("overview.filter_dimensions.clients") },
  ];

  // Track previous scenarios
  const prevScenariosRef = useRef([]);

  // Cache P&L data to avoid refetching on tab switch
  const cachedPnlData = useRef({});

  // Mouse interaction state
  const [mouseOverPLLine, setMouseOverPLLine] = useState(null);
  const [mouseOverPLLineFade, setMouseOverPLLineFade] = useState(null);
  const [mouseOverVal, setMouseOverVal] = useState(false);

  // Get available scenarios for selection
  const availableScenarios = scenarios.filter(
    (scenario) =>
      !selectedScenarios.some(
        (selected) => selected && selected.scenario_id === scenario.scenario_id,
      ),
  );

  // Get maximum scenarios allowed (P&L view supports 3 scenarios)
  const getMaxScenarios = () => {
    return 3;
  };

  // Force re-render when language changes
  const [, forceUpdate] = useState({});
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // Load P&L data for restored scenarios that don't have data yet
  useEffect(() => {
    const loadRestoredScenarios = async () => {
      const scenariosNeedingData = selectedScenarios.filter(
        (scenario) =>
          scenario &&
          !pnlData[scenario.scenario_id] &&
          !loadingStates[scenario.scenario_id] &&
          !errors[scenario.scenario_id],
      );

      if (scenariosNeedingData.length === 0) return;

      console.log(
        "📊 Loading P&L data for restored scenarios:",
        scenariosNeedingData.map((s) => s.scenario_name),
      );

      scenariosNeedingData.forEach(async (scenario) => {
        // Check cache first - use full filter arrays for cache key
        const filterKey = JSON.stringify(selectedFilters);
        const cacheKey = `${scenario.scenario_id}-${filterKey}`;

        if (cachedPnlData.current[cacheKey]) {
          console.log("✅ Using cached P&L data for:", scenario.scenario_name);
          const cached = cachedPnlData.current[cacheKey];

          // Find parent items to expand by default
          const parentItems = [];
          if (cached.plStructure && cached.plStructure.current) {
            cached.plStructure.current.forEach((item) => {
              if (item.is_parent) {
                parentItems.push(item.meta_name);
              }
            });
          }

          setDrillDownsOpen((prev) => ({
            ...prev,
            [scenario.scenario_id]: parentItems,
          }));

          setPnlData((prev) => ({
            ...prev,
            [scenario.scenario_id]: cached,
          }));

          return;
        }

        // Initialize drill-down state
        setDrillDownsOpen((prev) => ({
          ...prev,
          [scenario.scenario_id]: [],
        }));

        // Set loading state
        setLoadingStates((prev) => ({
          ...prev,
          [scenario.scenario_id]: true,
        }));

        try {
          // Pass filter arrays directly to POST endpoint
          const filters = {
            products: selectedFilters.products || [],
            brands: selectedFilters.brands || [],
            channels: selectedFilters.channels || [],
            regions: selectedFilters.regions || [],
            clients: selectedFilters.clients || [],
            pnl_flag: selectedPnlFlag || null, // Special parameter
          };

          const data = await getPnlData(scenario.scenario_id, filters);

          // Find parent items to expand by default
          const parentItems = [];
          if (data.plStructure && data.plStructure.current) {
            data.plStructure.current.forEach((item) => {
              if (item.is_parent) {
                parentItems.push(item.meta_name);
              }
            });
          }

          setDrillDownsOpen((prev) => ({
            ...prev,
            [scenario.scenario_id]: parentItems,
          }));

          setPnlData((prev) => ({
            ...prev,
            [scenario.scenario_id]: data,
          }));

          // Cache the result
          cachedPnlData.current[cacheKey] = data;

          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[scenario.scenario_id];
            return newErrors;
          });
        } catch (error) {
          console.error(
            "❌ Failed to fetch P&L data for restored scenario:",
            error,
          );
          setErrors((prev) => ({
            ...prev,
            [scenario.scenario_id]: error.message || "Failed to load P&L data",
          }));
        } finally {
          setLoadingStates((prev) => ({
            ...prev,
            [scenario.scenario_id]: false,
          }));
        }
      });
    };

    loadRestoredScenarios();
  }, [selectedScenarios]); // Only run when selectedScenarios changes

  const handleSelectScenario = async (scenarioId) => {
    const scenarioToAdd = scenarios.find(
      (scenario) => scenario.scenario_id === scenarioId,
    );

    if (!scenarioToAdd) return;

    const maxScenarios = getMaxScenarios();
    if (selectedScenarios.length >= maxScenarios) {
      toast.warning(t("pnl.max_scenarios_warning", { max: maxScenarios }));
      return;
    }

    const newScenarios = [...selectedScenarios, scenarioToAdd];
    setSelectedScenarios(newScenarios);

    // Initialize drill-down state
    setDrillDownsOpen((prev) => ({
      ...prev,
      [scenarioToAdd.scenario_id]: [],
    }));

    // Fetch P&L data
    setLoadingStates((prev) => ({
      ...prev,
      [scenarioToAdd.scenario_id]: true,
    }));

    try {
      // Pass filter arrays directly to POST endpoint
      const filters = {
        products: selectedFilters.products || [],
        brands: selectedFilters.brands || [],
        channels: selectedFilters.channels || [],
        regions: selectedFilters.regions || [],
        clients: selectedFilters.clients || [],
        pnl_flag: selectedPnlFlag || null, // Special parameter
      };

      const data = await getPnlData(scenarioToAdd.scenario_id, filters);

      // Find parent items to expand by default
      const parentItems = [];
      if (data.plStructure && data.plStructure.current) {
        data.plStructure.current.forEach((item) => {
          if (item.is_parent) {
            parentItems.push(item.meta_name);
          }
        });
      }

      setDrillDownsOpen((prev) => ({
        ...prev,
        [scenarioToAdd.scenario_id]: parentItems,
      }));

      setPnlData((prev) => ({
        ...prev,
        [scenarioToAdd.scenario_id]: data,
      }));

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[scenarioToAdd.scenario_id];
        return newErrors;
      });
    } catch (error) {
      console.error("❌ Failed to fetch P&L data:", error);
      setErrors((prev) => ({
        ...prev,
        [scenarioToAdd.scenario_id]: error.message || "Failed to load P&L data",
      }));
      toast.error(
        t("pnl.failed_to_load", { scenarioName: scenarioToAdd.scenario_name }),
      );
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [scenarioToAdd.scenario_id]: false,
      }));
    }
  };

  const handleRemoveScenario = (scenarioId) => {
    setSelectedScenarios((prev) =>
      prev.filter((scenario) => scenario.scenario_id !== scenarioId),
    );

    // Clean up data and states
    setPnlData((prev) => {
      const newData = { ...prev };
      delete newData[scenarioId];
      return newData;
    });

    setLoadingStates((prev) => {
      const newStates = { ...prev };
      delete newStates[scenarioId];
      return newStates;
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[scenarioId];
      return newErrors;
    });

    setDrillDownsOpen((prev) => {
      const newDrillDowns = { ...prev };
      delete newDrillDowns[scenarioId];
      return newDrillDowns;
    });
  };

  const createSetDrillDownsOpenForScenario = (scenarioId) => {
    return (newDrillDownsOpenOrUpdater) => {
      setDrillDownsOpen((prev) => {
        const currentArray = Array.isArray(prev[scenarioId])
          ? prev[scenarioId]
          : [];

        let newArray;
        if (typeof newDrillDownsOpenOrUpdater === "function") {
          newArray = newDrillDownsOpenOrUpdater(currentArray);
        } else {
          newArray = Array.isArray(newDrillDownsOpenOrUpdater)
            ? newDrillDownsOpenOrUpdater
            : [];
        }

        return {
          ...prev,
          [scenarioId]: newArray,
        };
      });
    };
  };

  const handleRetryScenario = async (scenarioId) => {
    const scenario = selectedScenarios.find(
      (s) => s.scenario_id === scenarioId,
    );
    if (!scenario) return;

    setLoadingStates((prev) => ({
      ...prev,
      [scenarioId]: true,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[scenarioId];
      return newErrors;
    });

    try {
      // Pass filter arrays directly to POST endpoint
      const filters = {
        products: selectedFilters.products || [],
        brands: selectedFilters.brands || [],
        channels: selectedFilters.channels || [],
        regions: selectedFilters.regions || [],
        clients: selectedFilters.clients || [],
        pnl_flag: selectedPnlFlag || null, // Special parameter
      };

      const data = await getPnlData(scenarioId, filters);

      const parentItems = [];
      if (data.plStructure && data.plStructure.current) {
        data.plStructure.current.forEach((item) => {
          if (item.is_parent) {
            parentItems.push(item.meta_name);
          }
        });
      }

      setDrillDownsOpen((prev) => ({
        ...prev,
        [scenarioId]: parentItems,
      }));

      setPnlData((prev) => ({
        ...prev,
        [scenarioId]: data,
      }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [scenarioId]: error.message || "Failed to load P&L data",
      }));
      toast.error(
        t("pnl.failed_to_reload", { scenarioName: scenario.scenario_name }),
      );
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [scenarioId]: false,
      }));
    }
  };

  // Mouse interaction handlers
  const handleMouseOverPLLine = (meta_name) => {
    setMouseOverPLLine(meta_name);
    if (selectedScenarios.length > 1) {
      setMouseOverPLLineFade(meta_name);
    }
  };

  const handleMouseLeavePLLine = () => {
    setMouseOverPLLine(null);
    setMouseOverPLLineFade(null);
  };

  const handleMouseOverPLVal = (meta_name) => {
    setMouseOverPLLine(meta_name);
    setMouseOverVal(true);
  };

  const handleMouseLeavePLVal = () => {
    setMouseOverPLLine(null);
    setMouseOverVal(false);
  };

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      const nonNullScenarios = selectedScenarios.filter((s) => s !== null);
      if (nonNullScenarios.length === 0) {
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setPnlFlagOptions([]);
        return;
      }

      setIsLoadingFilters(true);
      try {
        const response = await getPnlFilterOptions();
        if (response && response.products) {
          // Standard 5 filters
          setFilterOptions({
            products: response.products || [],
            brands: response.brands || [],
            channels: response.channels || [],
            regions: response.regions || [],
            clients: response.clients || [],
          });
          // Special parameter options
          setPnlFlagOptions(response.pnl_flags || []);
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setPnlFlagOptions([]);
      } finally {
        setIsLoadingFilters(false);
      }
    };

    fetchFilterOptions();
  }, [selectedScenarios]);

  // Refresh P&L data when filters change
  useEffect(() => {
    const refreshPnlDataWithFilters = async () => {
      const nonNullScenarios = selectedScenarios.filter((s) => s !== null);
      if (nonNullScenarios.length === 0) return;

      const prevScenarioIds = prevScenariosRef.current
        .map((s) => s?.scenario_id)
        .filter(Boolean);
      const currentScenarioIds = nonNullScenarios.map((s) => s.scenario_id);
      const scenariosChanged =
        prevScenarioIds.length !== currentScenarioIds.length ||
        !prevScenarioIds.every((id) => currentScenarioIds.includes(id)) ||
        !currentScenarioIds.every((id) => prevScenarioIds.includes(id));

      if (scenariosChanged) {
        prevScenariosRef.current = nonNullScenarios;
        return;
      }

      prevScenariosRef.current = nonNullScenarios;

      setLoadingStates((prev) => {
        const newStates = { ...prev };
        nonNullScenarios.forEach((scenario) => {
          newStates[scenario.scenario_id] = true;
        });
        return newStates;
      });

      const fetchPromises = nonNullScenarios.map(async (scenario) => {
        try {
          // Pass filter arrays directly to POST endpoint
          const filters = {
            products: selectedFilters.products || [],
            brands: selectedFilters.brands || [],
            channels: selectedFilters.channels || [],
            regions: selectedFilters.regions || [],
            clients: selectedFilters.clients || [],
            pnl_flag: selectedPnlFlag || null, // Special parameter
          };

          const data = await getPnlData(scenario.scenario_id, filters);

          return {
            scenarioId: scenario.scenario_id,
            data,
            success: true,
          };
        } catch (error) {
          return {
            scenarioId: scenario.scenario_id,
            error: error.message || "Failed to refresh P&L data",
            success: false,
          };
        }
      });

      const results = await Promise.allSettled(fetchPromises);

      results.forEach((result, index) => {
        const scenarioId = nonNullScenarios[index].scenario_id;

        if (result.status === "fulfilled") {
          const { data, success, error } = result.value;

          if (success) {
            setPnlData((prev) => ({
              ...prev,
              [scenarioId]: data,
            }));

            setErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[scenarioId];
              return newErrors;
            });
          } else {
            setErrors((prev) => ({
              ...prev,
              [scenarioId]: error,
            }));
          }
        } else {
          setErrors((prev) => ({
            ...prev,
            [scenarioId]: "Failed to refresh P&L data",
          }));
        }

        setLoadingStates((prev) => ({
          ...prev,
          [scenarioId]: false,
        }));
      });
    };

    refreshPnlDataWithFilters();
  }, [selectedFilters, selectedPnlFlag, selectedScenarios]);

  // Unified filter change handler
  const handleFiltersChange = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
  }, []);

  return (
    <Box className={classes.containerBox}>
      {/* Control Card */}
      <Card className={classes.controlCard}>
        <CardBody className={classes.controlCardBody}>
          <Box className={classes.controlCardContent}>
            {/* Left Section */}
            <Box className={classes.leftSection}>
              {/* PNL Flag Dropdown - Special parameter (positioned like volume selector) */}
              <Box className={classes.addScenarioBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="pnl-flag-selector-label" shrink>
                    {t("overview.considered_routes_label")}
                  </InputLabel>
                  <Select
                    labelId="pnl-flag-selector-label"
                    value={selectedPnlFlag}
                    onChange={(e) => setSelectedPnlFlag(e.target.value)}
                    label={t("overview.considered_routes_label")}
                    displayEmpty
                    disabled={
                      selectedScenarios.filter((s) => s !== null).length ===
                        0 || isLoadingFilters
                    }
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("pnl.all_routes")}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={{
                      className: classes.selectMenu,
                      classes: { paper: classes.selectPaper },
                    }}
                  >
                    <MenuItem value="" className={classes.selectMenuItem}>
                      <em>{t("pnl.all_routes")}</em>
                    </MenuItem>
                    {pnlFlagOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        className={classes.selectMenuItem}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Divider */}
              <Box className={classes.divider} />

              {/* FilterSystem Component - Standard 5 dimensions */}
              <Box className={classes.filtersBox}>
                <FilterSystem
                  dimensions={filterDimensions}
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  clientColor={clientColor}
                  disabled={
                    selectedScenarios.filter((s) => s !== null).length === 0 ||
                    isLoadingFilters
                  }
                />
              </Box>
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* P&L Display Area */}
      <FinancialImpactPnLDisplay
        selectedScenarios={selectedScenarios}
        availableScenarios={availableScenarios}
        folders={folders}
        isLoading={isLoading}
        pnlData={pnlData}
        loadingStates={loadingStates}
        errors={errors}
        drillDownsOpen={drillDownsOpen}
        mouseOverPLLine={mouseOverPLLine}
        mouseOverPLLineFade={mouseOverPLLineFade}
        mouseOverVal={mouseOverVal}
        clientColor={clientColor}
        onSelectScenario={handleSelectScenario}
        onRemoveScenario={handleRemoveScenario}
        onRetryScenario={handleRetryScenario}
        createSetDrillDownsOpenForScenario={createSetDrillDownsOpenForScenario}
        handleMouseLeavePLLine={handleMouseLeavePLLine}
        handleMouseOverPLLine={handleMouseOverPLLine}
        handleMouseLeavePLVal={handleMouseLeavePLVal}
        handleMouseOverPLVal={handleMouseOverPLVal}
      />
    </Box>
  );
}
